import React, { useMemo } from 'react';
import {
    PatchPayStatePayload,
    PayStateCreateFieldT,
    PayStateT,
} from 'routes/HumanResourcesModule/PayState/helpers/types';
import HRCreationLoader from 'routes/HumanResourcesModule/components/HRCreationLoader';
import MultiStepModal from 'ui/Modals/MultiStepModal';
import PayStateForm from './PayStateForm';
import { useTranslation } from 'react-i18next';
import { usePayStateFormState } from 'routes/HumanResourcesModule/PayState/PayStateHome/components/hooks/usePayStateFormState';
import {
    checkIsEtablissementsFilterActiveConsistencyWithSelectedEtablissements,
    checkIsPayBudgetsFilterActiveConsistencyWithSelectedPayBudgets,
} from 'routes/HumanResourcesModule/helpers/dataHelpers';

type Props = {
    show: boolean;
    onClose: () => void;
    payState?: PayStateT;
    editMode?: boolean;
    isLoading: boolean;
    isError: boolean;
    currentExercice: number;
    onSave: ({
        exercice,
        title,
        ...rest
    }: PayStateCreateFieldT | PatchPayStatePayload) => void;
};

export default function PayStateModal({
    show,
    onClose,
    editMode = false,
    currentExercice,
    payState = undefined,
    onSave,
    isLoading,
    isError,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    const headerTitle = payState?.exercice
        ? t('humanResources.editPayState')
        : t('humanResources.addingPayState');

    const exercice = payState?.exercice ?? currentExercice;

    const progressSteps = [
        t(`humanResources.payState.payStateCreation.dataLoad`),
        t(`humanResources.payState.payStateCreation.creation`),
    ];

    const [formState, setFormState] = usePayStateFormState(
        {
            title: payState?.title ?? '',
            selectedOrgChartHrsSoftwareIds:
                payState?.orgChartHrs.map((och) => och.softwareId) || [],
            selectedEtablissementSoftwareIds:
                payState?.etablissements.map((et) => et.softwareId) || [],
            isEtablissementsFilterActive:
                payState?.isEtablissementsFilterActive ?? false,
            isFormValid: false,
            isPayBudgetFilterActive: payState?.isPayBudgetFilterActive ?? false,
            selectedPayBudgetSoftwareIds:
                payState?.payBudgets.map((b) => b.softwareId) || [],
        },
        editMode,
    );

    const onClickSave = () => {
        onSave({
            title: formState.title,
            exercice,
            orgChartHrsSoftwareIds: formState.selectedOrgChartHrsSoftwareIds,
            etablissementSoftwareIds: formState.isEtablissementsFilterActive
                ? formState.selectedEtablissementSoftwareIds
                : [],
            isEtablissementsFilterActive:
                checkIsEtablissementsFilterActiveConsistencyWithSelectedEtablissements(
                    formState.isEtablissementsFilterActive,
                    formState.selectedEtablissementSoftwareIds,
                ),
            payBudgetSoftwareIds: formState.isPayBudgetFilterActive
                ? formState.selectedPayBudgetSoftwareIds
                : [],
            isPayBudgetFilterActive:
                checkIsPayBudgetsFilterActiveConsistencyWithSelectedPayBudgets(
                    formState.isPayBudgetFilterActive,
                    formState.selectedPayBudgetSoftwareIds,
                ),
        });
    };

    const firstStep = useMemo(
        () => (
            <PayStateForm
                formState={formState}
                setFormState={setFormState}
                headerTitle={headerTitle}
                exercice={exercice}
                editMode={editMode}
            />
        ),
        [formState, setFormState, headerTitle, editMode, exercice],
    );
    const secondStep = useMemo(
        () => (
            <HRCreationLoader
                isLoading={isLoading}
                isError={isError}
                headerTitle={headerTitle}
                progressSteps={progressSteps}
            />
        ),
        [isLoading, isError, headerTitle],
    );

    const steps = [
        {
            confirmAction: {
                enabled: formState.isFormValid,
                onClick: onClickSave,
            },
            stepBackAction: {
                label: 'common.cancel',
            },
            node: firstStep,
        },
        {
            confirmAction: {
                enabled: !isLoading,
                label: 'common.close',
            },
            stepBackAction: {
                enabled: false,
            },
            node: secondStep,
            disableClose: isLoading,
        },
    ];

    return (
        <MultiStepModal
            isOpen={show}
            onClose={onClose}
            maxWidth="sm"
            isSmallHeight
            steps={steps}
        />
    );
}
